import create, { ExecObject, ExecHandle, Router, ExecFunc } from './router.create';
import { ExtendedObject } from './info';
import Response from "./response";
export { create, ExecObject, ExecHandle, Router, ExecFunc}

// `${path}@${appId}`: Router
type RouterList<Ext extends ExtendedObject = {user: object}> = { [key: string]: Router<Ext> };

/** 获取剥离 Promise 后的值 */
type PromiseValue<T, S extends boolean = false> = T extends Promise<infer P> ? P : S extends true ? never : T;
/** 获取剥离 Response 后的值 */
type ResponseValue<T, S extends boolean = false> = T extends Response<infer P> ? P : S extends true ? never : T;

type MergeObjectParam<A,B, K extends string | number | symbol> =
	A extends {[k in K]?: infer TA} ? (B extends {[k in K]?: infer TB} ? TA | TB : TA) : B extends {[k in K]?: infer TB} ? TB : never; 
type MergeObject<A,B> = A extends object ? (B extends object ? {[k in keyof A | keyof B]?: MergeObjectParam<A, B, k>} : A | B) : A | B; 

export type ExecValue<E extends ExecFunc<any, any, any, any, any>> = E extends ExecFunc<any, infer V, any, any, any> ?  ResponseValue<PromiseValue<V>> : never;
export type ExecQuery<E extends ExecFunc<any, any, any, any, any>> = E extends ExecFunc<any, any, infer Q, any, any> ? Q : never;
export type ExecBody<E extends ExecFunc<any, any, any, any, any>> = E extends ExecFunc<any, any, any, infer B, any> ? B : never;
export type ExecData<E extends ExecFunc<any, any, any, any, any>> = E extends ExecFunc<any, any, any, any, infer D> ? D : never;
export type ExecQueryData<E extends ExecFunc<any, any, any, any, any>> = MergeObject<ExecQuery<E>, ExecData<E>>;
export type ExecBodyData<E extends ExecFunc<any, any, any, any, any>> = MergeObject<ExecBody<E>, ExecData<E>>;


export interface set<Ext extends ExtendedObject = {user: object}> {
	/**
	 * 设置路由
	 * @param path	路径
	 * @param f		函数集合
	 * @param r		自定义处理器
	 */
	(path: string, f: ExecObject<Ext>, r?: ExecHandle<Ext>): void;
	/**
	 * 设置路由
	 * @param path	路径
	 * @param appId	应用Id
	 * @param f		函数集合
	 * @param r		自定义处理器
	 */
	(path: string, appId: string, f: ExecObject<Ext>, r?: ExecHandle<Ext>): void;
}

export interface remove {
	/**
	 * 移除路由
	 * @param path	路径
	 * @param appId	应用Id
	 */
	(path: string, appId?: string): void;
}

export interface get<Ext extends ExtendedObject = {user: object}> {
	/**
	 * 获取路由
	 * @param path	路径
	 * @param appId	应用Id
	 */
	(path: string, appId?: string): Router<Ext>;
}



const list: RouterList = {};
/**
 * 设置路由
 * @param path	路径
 * @param appId	应用Id
 * @param f		函数集合
 * @param r		自定义处理器
 */
export function set<Ext extends ExtendedObject = {user: object}>(this: RouterList<Ext> | void, path: string, appId: string, f: ExecObject<Ext>, r?: ExecHandle<Ext>): void;
/**
 * 设置路由
 * @param path	路径
 * @param f		函数集合
 * @param r		自定义处理器
 */
export function set<Ext extends ExtendedObject = {user: object}>(this: RouterList<Ext> | void, path: string, f: ExecObject<Ext>, r?: ExecHandle<Ext>): void;
export function set<Ext extends ExtendedObject = {user: object}>(this: RouterList<Ext> | void, ...p: [string, string, ExecObject<Ext>, ExecHandle<Ext>?] | [string, ExecObject<Ext>, ExecHandle<Ext>?]): void {
	const routers: RouterList<Ext> = this || list;
	if (typeof p[1] !== 'string') {
		p.splice(1, 0, '');
	}
	const [path, appId, f, r] = p as [string, string, ExecObject<Ext>, ExecHandle<Ext>?];
	routers[appId ? path + '@' + appId : path] = create(f, r);
}

/**
 * 移除路由
 * @param path	路径
 * @param appId	应用Id
 */
export function remove<Ext extends ExtendedObject = {user: object}>(this: RouterList<Ext> | void, path: string, appId?: string): void {
	const routers: RouterList<Ext> = this || list;
	delete routers[appId ? path + '@' + appId : path];
}

/**
 * 获取路由
 * @param path	路径
 * @param appId	应用Id
 */
export function get<Ext extends ExtendedObject = {user: object}>(this: RouterList<Ext> | void, path: string, appId?: string): Router<Ext> {
	const routers: RouterList<Ext> = this || list;
	return routers[appId ? path + '@' + appId : path];
}
interface RouterApi<Ext extends ExtendedObject = {user: object}> {
	/** 创建路由 */
	create: create<Ext>,
	/** 设置路由 */
	set: set<Ext>,
	/** 移除路由 */
	remove: remove,
	/** 获取路由 */
	get: get<Ext>,
}
/**
 * 创建路由API
 * @param routers   默认的路由列表
 */
function RouterApi<Ext extends ExtendedObject = {user: object}>(routers: RouterList<Ext> = Object.create(null)): RouterApi<Ext> {
	return {
		create,
		set: set.bind(routers),
		remove: remove.bind(routers),
		get: get.bind(routers),
	}
}
export type api<Ext extends ExtendedObject = {user: object}> = RouterApi<Ext>;
export default RouterApi;

